import React, { createContext, useState, useEffect, useContext } from 'react';

const AdminAuthContext = createContext({});

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};

// Token storage keys
const ACCESS_TOKEN_KEY = 'stomp_admin_token';
const REFRESH_TOKEN_KEY = 'stomp_admin_refresh';
const ADMIN_USER_KEY = 'stomp_admin_user';

export const AdminAuthProvider = ({ children }) => {
  const [adminUser, setAdminUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load stored session on mount
  useEffect(() => {
    const loadStoredSession = async () => {
      try {
        const storedToken = localStorage.getItem(ACCESS_TOKEN_KEY);
        const storedUser = localStorage.getItem(ADMIN_USER_KEY);
        
        if (storedToken && storedUser) {
          // Verify token is still valid by checking expiry
          const tokenPayload = parseJwt(storedToken);
          const now = Math.floor(Date.now() / 1000);
          
          if (tokenPayload.exp > now) {
            setAdminUser(JSON.parse(storedUser));
          } else {
            // Token expired, try to refresh
            const refreshed = await refreshToken();
            if (!refreshed) {
              clearSession();
            }
          }
        }
      } catch (err) {
        console.error('Error loading admin session:', err);
        clearSession();
      } finally {
        setLoading(false);
      }
    };

    loadStoredSession();
  }, []);

  // Parse JWT to get payload
  const parseJwt = (token) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch {
      return { exp: 0 };
    }
  };

  // Clear session data
  const clearSession = () => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(ADMIN_USER_KEY);
    setAdminUser(null);
  };

  // Save session data
  const saveSession = (accessToken, refreshToken, user) => {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    localStorage.setItem(ADMIN_USER_KEY, JSON.stringify(user));
    setAdminUser(user);
  };

  // Get current access token
  const getAccessToken = () => {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  };

  // Login function
  const login = async (email, password) => {
    setError(null);
    try {
      const response = await fetch('/api/admin/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      saveSession(data.accessToken, data.refreshToken, data.user);
      return { success: true, user: data.user };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Logout function
  const logout = async () => {
    try {
      const token = getAccessToken();
      if (token) {
        await fetch('/api/admin/auth/logout', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
      }
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      clearSession();
    }
  };

  // Refresh token function
  const refreshToken = async () => {
    try {
      const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (!storedRefreshToken) return false;

      const response = await fetch('/api/admin/auth/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken: storedRefreshToken }),
      });

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      saveSession(data.accessToken, data.refreshToken, data.user);
      return true;
    } catch (err) {
      console.error('Token refresh failed:', err);
      return false;
    }
  };

  // Make authenticated API request
  const apiRequest = async (url, options = {}) => {
    let token = getAccessToken();
    
    // Check if token is expired
    if (token) {
      const payload = parseJwt(token);
      const now = Math.floor(Date.now() / 1000);
      
      // Refresh if expiring in less than 5 minutes
      if (payload.exp - now < 300) {
        const refreshed = await refreshToken();
        if (refreshed) {
          token = getAccessToken();
        } else {
          clearSession();
          throw new Error('Session expired');
        }
      }
    }

    const response = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${token}`,
      },
    });

    // Handle 401 - try to refresh and retry once
    if (response.status === 401) {
      const refreshed = await refreshToken();
      if (refreshed) {
        token = getAccessToken();
        return fetch(url, {
          ...options,
          headers: {
            ...options.headers,
            'Authorization': `Bearer ${token}`,
          },
        });
      } else {
        clearSession();
        throw new Error('Session expired');
      }
    }

    return response;
  };

  const value = {
    adminUser,
    loading,
    error,
    login,
    logout,
    apiRequest,
    isAuthenticated: !!adminUser,
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};